import type MapFeature from '@/lib/map-feature'
import type { MapMouseEvent } from '@/lib/map-x/webmap-viewer-types'

import router from '@/router'

import Geographic from '@/lib/geographic'
import { CARD_ORIGINS, MARKUP_VIEW_ORIGINS } from '@/lib/enums-marketing'
import { error } from '@/lib/logger'

import markupService from '@/services/markup-service'
import pubsub from '@/lib/pubsub'

/**
 * *(AI-generated)*
 * Handles a non-markup map click by navigating to the general query overview card for the current app.
 */
export const handleNonMarkupClick = (event: MapMouseEvent, zoom: number) => {
  // If the user is clicking where the card will open up animate the map
  // to center the query point so that it's not hidden by the card
  // The card is about 500 px wide
  const isPointBehindCard = event.point.x < 500
  markupService.clearFocusedMarkup()

  // Fallback on a generic map query
  const coord =
    event.lngLat && zoom
      ? `${Geographic.fromMapbox(event.lngLat).toURLCoordParam()},${zoom}`
      : null

  router
    .push({
      name: `query-fish-overview`,
      params: {
        coord: coord,
      },
      state: { animateMapTo: isPointBehindCard, origin: CARD_ORIGINS.MAP_TAP },
    })
    .catch((err) => {
      error('Router', 'Failed to navigate to trail', err)
    })
}

/**
 * *(AI-generated)*
 * Handles a click on a markup feature by focusing it and navigating to the appropriate markup card.
 */
export const handleMarkupClick = (features: MapFeature[]) => {
  const markupGeoJson = features[0]
  const uuid = markupGeoJson.id
  markupService.focusMarkup(uuid)
  const markupToOpen = markupService.getMarkupByID(uuid)
  const typeRoute = markupToOpen?.type === 'track' ? 'line' : markupToOpen?.type
  router
    .push({
      name: `fish-${typeRoute}`,
      params: {
        id: uuid,
      },
      state: { origin: MARKUP_VIEW_ORIGINS.MAP_VIEW },
    })
    .catch(() => {})
}

/**
 * *(AI-generated)*
 * Handles a click on an offline map feature and navigates to the offline map details card.
 */
export const handleOfflineMapClick = (features: MapFeature[]) => {
  const mapGeoJson = features[0]
  const app = 'fish'
  const mapId = mapGeoJson.properties.id
  const path = `/${app}/map/offline/details/${mapId}`
  router.push({ path }).catch((err) => {
    error('Router', 'Failed to navigate to offline map', err)
  })
}

/**
 * Handle the click of a search result pin on the map
 */
export const handleSearchResultClick = (features: MapFeature[]) => {
  const feature = features[0]
  if (feature) {
    pubsub.publish('search-map-marker-selected', feature.properties?.id)
  }
}
